import { useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { LuChevronDown, LuKeyRound, LuLogOut, LuSettings, LuUserCog } from 'react-icons/lu';
import { useAuth } from '@/features/auth/AuthProvider';
import { ROLE_LABELS } from '@/features/auth/permissions';
import { useClickOutside } from '@/hooks/useClickOutside';
import { Badge } from '../ui/Badge';

function initials(name: string) {
  return name.split(/\s+/).map((p) => p[0]).slice(0, 2).join('').toUpperCase();
}

export function UserMenu() {
  const { user, signOut, isDemo } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useClickOutside(ref, () => setOpen(false), open);
  if (!user) return null;

  return (
    <div ref={ref} className="relative">
      <button onClick={() => setOpen((o) => !o)} className="flex items-center gap-2 rounded-md py-1 pl-1 pr-2 hover:bg-slate-100" aria-expanded={open} aria-label="User menu">
        <span className="flex size-8 items-center justify-center rounded-full bg-brand-800 text-xs font-semibold text-white">{initials(user.fullName || user.email)}</span>
        <span className="hidden text-left leading-tight md:block">
          <span className="block max-w-36 truncate text-sm font-medium text-slate-800">{user.fullName}</span>
          <span className="block text-[11px] text-slate-500">{ROLE_LABELS[user.role]}</span>
        </span>
        <LuChevronDown className="hidden size-4 text-slate-400 md:block" />
      </button>
      {open && (
        <div className="absolute right-0 top-11 z-40 w-64 overflow-hidden rounded-lg border border-slate-200 bg-white shadow-xl">
          <div className="border-b border-slate-100 px-4 py-3">
            <p className="truncate text-sm font-semibold text-slate-900">{user.fullName}</p>
            <p className="truncate text-xs text-slate-500">{user.email}</p>
            <div className="mt-2 flex flex-wrap gap-1.5">
              <Badge tone="brand">{ROLE_LABELS[user.role]}</Badge>
              {user.departmentName && <Badge>{user.departmentName}</Badge>}
            </div>
          </div>
          <div className="py-1 text-sm">
            <Link to="/account" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-50">
              <LuKeyRound className="size-4" /> My account & password
            </Link>
            <Link to="/settings" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-50">
              <LuSettings className="size-4" /> Settings
            </Link>
            {isDemo && (
              <Link to="/settings#demo-role" onClick={() => setOpen(false)} className="flex items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-50">
                <LuUserCog className="size-4" /> Switch demo role
              </Link>
            )}
            {!isDemo && (
              <button onClick={() => void signOut()} className="flex w-full items-center gap-2 px-4 py-2 text-slate-700 hover:bg-slate-50">
                <LuLogOut className="size-4" /> Sign out
              </button>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
